import { Component, ElementRef, ViewChild, inject, AfterViewInit, OnDestroy, Input } from '@angular/core';
import { ModalController, NavController, IonicModule, GestureController, MenuController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { Charity } from '../../../core/models/Charity';
import { CharityService } from '../../../core/services/charity/charity.service';
import { TabService } from '../../../core/services/navigation/tab.service';
import { StateType } from '../../../core/models/StateType';
import { Subscription } from 'rxjs';
import { Gesture, GestureDetail } from '@ionic/angular';

@Component({
  selector: 'app-charity-selector-bottom-sheet',
  templateUrl: './charity-selector-bottom-sheet.component.html',
  styleUrls: ['./charity-selector-bottom-sheet.component.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule]
})
export class CharitySelectorBottomSheetComponent implements AfterViewInit, OnDestroy {
  @ViewChild('gestureArea', { read: ElementRef }) gestureArea!: ElementRef;

  private modalController = inject(ModalController);
  private navController = inject(NavController);
  private charityService = inject(CharityService);
  private tabService = inject(TabService);
  private gestureController = inject(GestureController);
  private menuController = inject(MenuController);

  @Input() permittedCharities: Charity[] = [];
  @Input() currentCharityId: string | null = null;
  isSwitching: boolean = false;
  imageLoadedMap: { [key: string]: boolean } = {};
  private gesture!: Gesture;

  ngAfterViewInit() {
    this.setupGesture();
  }

  ngOnDestroy() {
    if (this.gesture) {
      this.gesture.destroy();
    }
  }

  setupGesture() {
    if (!this.gestureArea) return;

    this.gesture = this.gestureController.create({
      el: this.gestureArea.nativeElement,
      threshold: 15,
      gestureName: 'swipe-down',
      direction: 'y',
      onMove: (detail: GestureDetail) => this.onMove(detail),
      onEnd: (detail: GestureDetail) => this.onEnd(detail),
    });
    this.gesture.enable();
  }

  onMove(detail: GestureDetail) {
    if (detail.deltaY > 0) {
      this.gestureArea.nativeElement.style.transform = `translateY(${detail.deltaY}px)`;
    }
  }

  onEnd(detail: GestureDetail) {
    const shouldClose: boolean = detail.deltaY > 100;
    
    if (shouldClose) {
      this.modalController.dismiss({ selected: false });
    } else {
      this.gestureArea.nativeElement.style.transform = '';
    }
  }

  selectCharity(charity: Charity) {
    if (this.isSwitching || charity.id === this.currentCharityId) {
      return;
    }

    this.isSwitching = true;
    const targetCharityId: string = charity.id ?? '';
    
    this.charityService.setCurrentCharity(targetCharityId);
    
    const charitySubscription: Subscription = this.charityService.charity$.subscribe({
      next: (loadedCharity) => {
        if (loadedCharity && loadedCharity.id === targetCharityId) {
          const lastPage: { url: string; state: StateType } | null = this.tabService.getLastPage('charity');
          
          this.modalController.dismiss({ selected: true }).then(() => {
            this.navController.navigateRoot(lastPage?.url || '/charity/charity-info', {
              state: lastPage?.state || {},
            });
          });
          
          charitySubscription.unsubscribe();
          this.isSwitching = false;
        }
      },
      error: (error) => {
        console.error('Error switching to charity:', error);
        this.isSwitching = false;
        charitySubscription.unsubscribe();
      }
    });
  }

  onImageLoad(charityId: string) {
    this.imageLoadedMap[charityId] = true;
  }

  cancel() {
    this.modalController.dismiss({ selected: false });
  }
}